import { DEFAULT_LOCALE, type Locale } from "./locales";

export type StringEntry = Record<typeof DEFAULT_LOCALE, string> &
  Partial<Record<Locale, string>>;

/**
 * Per-key, per-locale string dictionary.
 *
 * Every entry must include a {@link DEFAULT_LOCALE} string — that's the
 * guaranteed fallback `t()` returns when the target locale is missing.
 * Other locales are optional. Subsequent PRs populate this dictionary as
 * call sites migrate.
 */
export const strings = {
  "address.postal_code.label": {
    "en-US": "Zip code",
    "en-AU": "Postcode",
  },
  "address.postal_code.placeholder": {
    "en-US": "10001",
    "en-AU": "2000",
  },
  "address.postal_code.invalid": {
    "en-US": "Invalid zip code",
    "en-AU": "Invalid postcode",
  },
  "address.postal_code.blank": {
    "en-US": "Zip code can't be blank",
    "en-AU": "Postcode can't be blank",
  },
  "address.summary.required": {
    "en-US": "Home address, city, state, and zip code are required.",
    "en-AU": "Home address, city, state, and postcode are required.",
  },
  "card.postal_code.label": {
    "en-US": "Zip code",
    "en-AU": "Postcode",
  },
  "card.postal_code.invalid": {
    "en-US": "Invalid ZIP code",
    "en-AU": "Invalid postcode",
  },

  // --- AU launch terminology ---------------------------------------------
  // AU does not use the "Reshop fee" / "Reshop refund" / "instant refund"
  // brand terms. US keeps the original copy byte-for-byte via the en-US entry.

  // "Reshop fee" -> "Service fee"
  "refund.fee.label": {
    "en-US": "Reshop fee",
    "en-AU": "Service fee",
  },
  "refund.fee.label_title": {
    "en-US": "Reshop Fee",
    "en-AU": "Service Fee",
  },
  "refund.fee.info_label": {
    "en-US": "Reshop fee information",
    "en-AU": "Service fee information",
  },
  "cancellation.fee_notice": {
    "en-US":
      "Decided to keep your return? Cancel your refund below. Note the {fee} Reshop fee covers your original refund and is not returned when your refund is canceled.",
    "en-AU":
      "Decided to keep your return? Cancel your refund below. Note the {fee} service fee covers your original refund and is not returned when your refund is cancelled.",
  },
  "refund.fee.tooltip": {
    "en-US":
      "Reshop charges a small fee to send your refund to some destinations.",
    "en-AU":
      "Reshop charges a small service fee to send your refund to some destinations.",
  },

  // "Reshop refund" -> "refund"
  "refund.breakdown.amount_label": {
    "en-US": "Reshop refund amount",
    "en-AU": "Refund amount",
  },
  "refund.timeout.error": {
    "en-US": "We were unable to complete your Reshop refund at this time.",
    "en-AU": "We were unable to complete your refund at this time.",
  },
  "shipping_info.no_return_needed": {
    "en-US":
      "It looks like you don’t need to return this item to the retailer. Once you get your Reshop refund; you’re all set!",
    "en-AU":
      "It looks like you don’t need to return this item to the retailer. Once you get your refund; you’re all set!",
  },
  "reshop_card.next_refund": {
    "en-US":
      "To optimize your refund - use leftover balance for small, everyday purchases or add to it with your next Reshop refund.",
    "en-AU":
      "To optimize your refund - use leftover balance for small, everyday purchases or add to it with your next refund.",
  },
  "profile_legalese.sms_notices": {
    "en-US":
      "Reshop refund SMS service notices (msg freq varies), and that separate unreturned item charges may apply.",
    "en-AU":
      "refund SMS service notices (msg freq varies), and that separate unreturned item charges may apply.",
  },
  "refund_status.cancelled_without_repurchase": {
    "en-US":
      "Your Reshop refund has been canceled. If you still return your items, you will get your refund from the retailer directly.",
    "en-AU":
      "Your refund has been canceled. If you still return your items, you will get your refund from the retailer directly.",
  },
  "refund_status.cancelled_without_repurchase.canceled_only": {
    "en-US": "Your Reshop refund has been canceled.",
    "en-AU": "Your refund has been canceled.",
  },
  "refund_status.reversed_cancelled": {
    "en-US":
      "We've successfully canceled and reversed your Reshop refund, so you're all settled up!",
    "en-AU":
      "We've successfully canceled and reversed your refund, so you're all settled up!",
  },
  "refund_status.cancelled_payment_due": {
    "en-US":
      "It looks like you canceled your return, so your Reshop refund is being reversed.",
    "en-AU":
      "It looks like you canceled your return, so your refund is being reversed.",
  },
  "refund_status.rejected_payment_due": {
    "en-US":
      "{merchantName} has rejected your return, so your Reshop refund is being reversed.",
    "en-AU":
      "{merchantName} has rejected your return, so your refund is being reversed.",
  },
  "cancellation.button.still_cancel": {
    "en-US": "I still want to cancel my Reshop refund",
    "en-AU": "I still want to cancel my refund",
  },
  "cancellation.button.cancel": {
    "en-US": "Cancel my Reshop refund",
    "en-AU": "Cancel my refund",
  },
  "cancellation.not_returning.heading": {
    "en-US":
      "Not returning the item(s)? Go ahead and cancel your Reshop refund below",
    "en-AU": "Not returning the item(s)? Go ahead and cancel your refund below",
  },
  "claim_expired.heading": {
    "en-US": "Your Reshop refund request has expired",
    "en-AU": "Your refund request has expired",
  },
  "claim_expired.why_link": {
    "en-US": "Why did my Reshop refund expire?",
    "en-AU": "Why did my refund expire?",
  },

  // "instant refund" / "instantly" removed for AU (AU refunds are not instant)
  "refund.breakdown.total_label": {
    "en-US": "Your instant refund total",
    "en-AU": "Your refund total",
  },
  "refund.issued_instantly_to": {
    "en-US": "Your refund will be issued instantly to",
    "en-AU": "Your refund will be issued instantly to",
  },
  "shipping_info.return_needed": {
    "en-US":
      "Remember to ship your item{plural} by the below date to keep your instant refund and avoid an unreturned item charge.",
    "en-AU":
      "Remember to ship your item{plural} by the below date to keep your refund and avoid an unreturned item charge.",
  },
  "payout_method.ofp_failure_ofp_only": {
    "en-US":
      "We couldn’t issue an instant refund to this card, but your return has been submitted. You’ll receive a refund from {merchantName} after you return your item(s).",
    "en-AU":
      "We couldn’t issue a refund to this card, but your return has been submitted. You’ll receive a refund from {merchantName} after you return your item(s).",
  },
  "payout_method.ofp_failure_generic": {
    "en-US":
      "We couldn’t send your refund to this card. Please add a debit card or bank account instead to receive your instant refund.",
    "en-AU":
      "We couldn’t send your refund to this card. Please add a debit card or bank account instead to receive your refund.",
  },
  "risk_declined.heading": {
    "en-US": "This return isn’t approved for an instant refund.",
    "en-AU": "This return isn’t approved for a refund.",
  },
  "cancellation.success.heading": {
    "en-US": "Your instant refund has been canceled.",
    "en-AU": "Your refund has been canceled.",
  },
  "cancellation.reason.prompt": {
    "en-US":
      "Can you tell us more about why you no longer want an instant refund?",
    "en-AU": "Can you tell us more about why you no longer want a refund?",
  },
  "claim_success.reshop_card_heading": {
    "en-US": "You received an instant refund!",
    "en-AU": "You received a refund!",
  },
  "reshop_card.availability": {
    "en-US":
      "When you choose to get your refund on a Reshop Card, it will be available instantly.",
    "en-AU":
      "When you choose to get your refund on a Reshop Card, it will be available instantly.",
  },
  "claim_ofp_error.widget_ofp_only": {
    "en-US": "We weren't able to issue an instant refund due to an error.",
    "en-AU": "We weren't able to issue a refund due to an error.",
  },
  "claim_ofp_error.non_ofp_only": {
    "en-US":
      "We couldn't process your refund to your original form of payment due to an error. Please choose a different payment method to get your instant refund.",
    "en-AU":
      "We couldn't process your refund to your original form of payment due to an error. Please choose a different payment method to get your refund.",
  },
  "payout_fee.tooltip.rate": {
    "en-US":
      "A {feeRate}%{maxFee} fee allows us to issue your refund instantly to your {destination}. Quick and easy — no new accounts to link.",
    "en-AU":
      "A {feeRate}%{maxFee} service fee to receive your refund straight to your {destination}. Quick and easy — no new accounts to link.",
  },
  "payout_fee.tooltip.flat": {
    "en-US":
      "A {fee} fee allows us to issue your refund instantly to your {destination}. Quick and easy — no new accounts to link.",
    "en-AU":
      "A {fee} service fee to receive your refund straight to your {destination}. Quick and easy — no new accounts to link.",
  },
  "payout.reshop_card.decision": {
    "en-US":
      "Reshop instant refund requests are typically decisioned by Reshop in a matter of seconds, and are approved by payment to your Reshop Card.",
    "en-AU":
      "Reshop refund requests are typically decisioned by Reshop in a matter of seconds, and are approved by payment to your Reshop Card.",
  },
  "payout.ofp.timing": {
    "en-US":
      "Refunds are issued instantly by Reshop. Depending on your bank, it may take up to 3-5 business days to be visible in your account.",
    "en-AU":
      "Refunds are issued by Reshop. Depending on your bank, it may take up to 3-5 business days to be visible in your account.",
  },
  "payout.bank.timing": {
    "en-US":
      "Refunds are issued instantly to your bank account and typically take 1 business day to be visible.",
    "en-AU":
      "Refunds are issued to your bank account and typically take 1 business day to be visible.",
  },

  // "Terms of Service" -> "Consumer Terms & Conditions" (link text; URL is
  // localized separately via legalLinks.ts)
  "legal.terms_of_service": {
    "en-US": "Terms of Service",
    "en-AU": "Consumer Terms & Conditions",
  },
} satisfies Record<string, StringEntry>;

export type StringKey = keyof typeof strings;
